<template>
  <div ref="contentRef">
    <div class="header">
      <div>
        <WdsHeading type="title1">{{ $t('Share your job') }}</WdsHeading>
        <WdsText secondary large>{{ $t(subHeaderText) }}</WdsText>
      </div>
      <BaseButton class="header-button">
        <a :href="buttonUrl">
          {{ isEdit ? $t('Go back') : $t('Skip and go to job') }}
        </a>
      </BaseButton>
    </div>
    <div v-if="shareWith === ALL">
      <BaseButton
        :color="selectedContactType === ALL ? ColorSchemes.SUCCESS : ColorSchemes.DEFAULT"
        size="sm"
        class="table-filter-button"
        @click="selectedContactType = ALL"
      >
        {{ $t('All') }}
      </BaseButton>
      <BaseButton
        :color="
          selectedContactType === TRUSTED_CONTACT ? ColorSchemes.SUCCESS : ColorSchemes.DEFAULT
        "
        size="sm"
        class="table-filter-button"
        @click="selectedContactType = TRUSTED_CONTACT"
      >
        {{ $t('Trusted Contacts') }}
      </BaseButton>
      <BaseButton
        :color="selectedContactType === RECRUITER ? ColorSchemes.SUCCESS : ColorSchemes.DEFAULT"
        size="sm"
        class="table-filter-button"
        @click="selectedContactType = RECRUITER"
      >
        {{ $t('Recruiters') }}
      </BaseButton>
    </div>
    <BaseDataTable
      class="bgm-white data-table"
      :columns="columns"
      :entries="contactsToUse"
      :pre-selected-rows="preSelectedContactRows"
      :default-page-length="10"
      tall-rows
      multi-select
      preserve-selected-rows
      :search-placeholder="$t('Search names and tags')"
      @row-click="toggleContact"
      @rows-selected="selectedRows"
      @input="handleDatatableInput"
    >
      <template #no-results>
        <div class="m-t-20 wrapper">
          <div class="circle search-circle"><i class="fa fa-search"></i></div>
          <div class="no-contacts-header">{{ $t(noContactsHeaderText) }}</div>
          <div class="no-contacts-subheader">
            {{ $t(noContactsSubHeaderText) }}
          </div>
          <div class="tw-mt-8 tw-flex tw-justify-center tw-gap-sm">
            <div
              v-if="selectedContactType !== RECRUITER"
              :class="selectedContactType === ALL ? 'tw-flex tw-w-1/2 tw-justify-end' : ''"
            >
              <a :href="addTrustedContactsUrl">
                <BaseButton size="sm" :color="ColorSchemes.SUCCESS">
                  {{ $t('Add trusted contact') }}
                </BaseButton>
              </a>
            </div>
            <div
              v-if="selectedContactType !== TRUSTED_CONTACT"
              :class="selectedContactType === ALL ? 'tw-flex tw-w-1/2' : ''"
            >
              <a :href="addRecruitersUrl">
                <BaseButton size="sm" :color="ColorSchemes.SUCCESS">
                  {{ $t('Add recruiter') }}
                </BaseButton>
              </a>
            </div>
          </div>
        </div>
      </template>

      <template #Name="{ row }">
        <div class="row-name">
          <WdsAvatar
            :path="getAvatarPath(row.avatar_path, row.globalId)"
            :initials="row.initials"
            md
            class="row-avatar"
          />
          <div>
            {{ row.name }}
            <div v-if="row.email" class="contact-secondary">
              {{ row.email }}
            </div>
          </div>
        </div>
      </template>

      <template #Status="{ row }">
        <WdsTag sm :success="row.status === 'Active'">{{ row.status }}</WdsTag>
      </template>

      <template #Tags="{ row }">
        <span v-if="row.tags.length">
          <span v-for="tag in row.tags" :key="tag.tag.name" class="tag">
            <WdsTag sm primary>{{ tag.tag.name }}</WdsTag>
          </span>
          <span v-if="row.countAdditionalTags > 0">
            <WdsTag sm>+{{ row.countAdditionalTags }}</WdsTag>
          </span>
        </span>
        <span v-else />
      </template>

      <template #ContactType="{ row }">
        <div class="contact-secondary">
          {{ row.contactType === RECRUITER ? $t('Recruiter') : $t('Trusted contact') }}
        </div>
      </template>
    </BaseDataTable>
    <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
    <BaseHtmlForm
      ref="invitationForm"
      :action="formAction"
      method="PATCH"
      append-user-and-account
      @submit="submitting = true"
    >
      <template v-if="selectedContactsObjects.length > 0">
        <div v-for="contact in selectedContactsObjects" :key="contact.globalId">
          <input v-model="contact.id" type="hidden" name="selected-contacts[]" />
        </div>
      </template>
    </BaseHtmlForm>

    <div
      ref="contacts"
      class="contacts-selected m-t-20"
      :class="{ 'tw-fixed': footerOutOfView }"
      :style="`width: ${contactsSelectedWidth}`"
    >
      <SelectedContactAvatars :selected-contacts="selectedContactsObjects" :max-avatars="6" />
      <BaseButton color="primary" size="sm" :disabled="submitIsDisabled()" @click="onClickSubmit">
        {{ isEdit ? $t('Update invitations') : $t('Invite to my job') }}
      </BaseButton>
    </div>
  </div>
</template>

<script>
import { WdsTag, WdsAvatar, WdsText, WdsHeading, WdsStack } from '@worksome/wds-components'
import BaseDataTable from '@/components/BaseDataTable'
import BaseButton from '@/components/BaseButton.vue'
import SelectedContactAvatars from '@/components/SelectedContactAvatars.vue'
import { ColorSchemes } from '@/utils/enum'
import { getAvatarPath } from '@/utils/avatar'

const ALL = 'All'
const TRUSTED_CONTACT = 'company_freelancer'
const RECRUITER = 'company_recruiter'

function getShareWith(shareWithTrustedContacts, shareWithRecruiters) {
  if (shareWithTrustedContacts && shareWithRecruiters) {
    return ALL
  }

  if (shareWithTrustedContacts) {
    return TRUSTED_CONTACT
  }

  if (shareWithRecruiters) {
    return RECRUITER
  }

  // fallback
  return ALL
}

export default {
  components: {
    BaseButton,
    BaseDataTable,
    SelectedContactAvatars,
    WdsTag,
    WdsAvatar,
    WdsText,
    WdsHeading,
    // eslint-disable-next-line vue/no-unused-components -- Baseline eslint ignore please fix if working on this
    WdsStack,
  },
  props: {
    yjob: { type: Object, default: () => {} },
    formAction: { type: String, default: '' },
    contacts: { type: Array, default: () => [] },
    preSelectedContacts: { type: Array, default: () => [] },
    isEdit: { type: Boolean, default: false },
    shareWithTrustedContacts: { type: Boolean, default: false },
    shareWithRecruiters: { type: Boolean, default: false },
    buttonUrl: { type: String, default: '' },
    addTrustedContactsUrl: { type: String, default: '' },
    addRecruitersUrl: { type: String, default: '' },
  },
  data() {
    return {
      filteredContacts: [...this.contacts],
      selectedContactType: getShareWith(this.shareWithTrustedContacts, this.shareWithRecruiters),
      selectedContacts: [],
      selectedContactsObjects: [],
      filteredRows: {}, // BaseDataTable rows.data object
      csrf: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      submitting: false,
      columns: [
        {
          slot: 'Name',
          key: 'name',
          title: $t('Name'),
          orderAble: true,
          searchKeys: ['name', 'tags.tag.name'],
          class: 'vmiddle',
        },
        {
          slot: 'Status',
          key: 'status',
          title: $t('Status'),
          class: 'vmiddle',
        },
        {
          slot: 'Tags',
          key: 'hasAlreadyReceivedInvite',
          title: $t('Tags'),
          class: 'vmiddle',
        },
        {
          slot: 'ContactType',
          key: 'contactType',
          title: $t('Contact type'),
          class: 'vmiddle',
        },
      ],
      maxTags: 3,
      ColorSchemes: Object.freeze(ColorSchemes),
      contactsSelectedWidth: '',
      footerOutOfView: false,
      ALL,
      TRUSTED_CONTACT,
      RECRUITER,
    }
  },
  computed: {
    shareWith() {
      return getShareWith(this.shareWithTrustedContacts, this.shareWithRecruiters)
    },
    contactsToUse() {
      return this.filteredContacts.map((contact) => ({
        ...contact,
        countAdditionalTags: contact.tags.length - this.maxTags,
        tags: contact.tags.slice(0, this.maxTags),
        isPreSelected: this.preSelectedContacts.includes(contact.globalId),
      }))
    },
    preSelectedContactRows() {
      return this.contactsToUse.filter((row) => row.isPreSelected)
    },
    subHeaderText() {
      switch (this.shareWith) {
        case TRUSTED_CONTACT:
          return 'Select the Trusted Contacts you want to share the job with here'
        case RECRUITER:
          return 'Select the recruiters you want to share the job with here'
        case ALL:
        default:
          return 'Select the recruiters and Trusted Contacts you want to share the job with here'
      }
    },
    noContactsHeaderText() {
      let contactType = ''
      switch (this.selectedContactType) {
        case TRUSTED_CONTACT:
          contactType = 'trusted contacts'
          break
        case RECRUITER:
          contactType = 'recruiters'
          break
        case ALL:
        default:
          contactType = 'contacts'
          break
      }

      return `No ${contactType} found`
    },
    noContactsSubHeaderText() {
      let types = ''
      switch (this.selectedContactType) {
        case TRUSTED_CONTACT:
          types = 'Trusted Contacts'
          break
        case RECRUITER:
          types = 'Recruiters'
          break
        case ALL:
        default:
          types = 'Trusted Contacts or recruiters'
          break
      }

      return `Add ${types} to your contact lists so you can invite them to your job`
    },
  },
  watch: {
    shareWith(newValue) {
      if (newValue === ALL) {
        this.selectedContactType = ALL
      }
    },
    selectedContactType: {
      handler(newSelectedOption) {
        this.filteredContacts =
          newSelectedOption === ALL
            ? this.contacts
            : this.contacts.filter((contact) => contact.contactType === newSelectedOption)
      },
    },
  },
  created() {
    this.selectedContacts = this.preSelectedContactRows.map((r) => r.globalId)
    this.selectedContactsObjects = this.preSelectedContactRows.map((r) => ({
      ...r,
      id: r.globalId,
    }))

    window.addEventListener('resize', this.updateContactsSelectedStyle)
  },
  mounted() {
    this.updateContactsSelectedStyle()
  },
  methods: {
    getAvatarPath,
    updateContactsSelectedStyle() {
      this.updateContactsSelectedWidth()
      this.updateContactsSelectedPosition()
    },
    updateContactsSelectedPosition() {
      this.footerOutOfView = false
      this.$nextTick(() => {
        const rect = this.$refs.contacts.getBoundingClientRect()
        this.footerOutOfView = !(rect.bottom + window.pageYOffset <= window.innerHeight)
      })
    },
    updateContactsSelectedWidth() {
      this.contactsSelectedWidth = `${this.$refs.contentRef.offsetWidth}px`
    },
    selectedRows(rows) {
      this.selectedContacts = Object.values(rows).map((r) => r.globalId)
      this.selectedContactsObjects = Object.values(rows).map((r) => ({ ...r, id: r.globalId }))
    },
    toggleContact(contact) {
      const { globalId } = contact
      if (!this.selectedContacts.includes(globalId)) {
        this.selectedContacts.push(globalId)
      } else {
        this.selectedContacts = this.selectedContacts.filter((c) => c !== globalId)
      }
    },
    handleDatatableInput($event) {
      this.updateContactsSelectedPosition()
      this.filteredRows = $event
    },
    submitIsDisabled() {
      return (
        !this.selectedContactsObjects.length ||
        (this.selectedContactsObjects.length === this.preSelectedContacts.length &&
          this.selectedContactsObjects.every((c) => c.isPreSelected)) ||
        this.submitting
      )
    },
    onClickSubmit() {
      this.$refs.invitationForm.submit()
      this.submitting = true
    },
  },
}
</script>

<style scoped lang="scss">
@import 'resources/sass/frontend/variable-overrides';
@import 'resources/sass/frontend/_color-variables';

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 48px;

  .header-button {
    height: 100%;
  }
}

.contact-secondary {
  color: $gray-700;
}

.row-avatar {
  margin-right: 16px;
}

.row-name {
  display: flex;
  align-items: center;
}

.no-contacts-header {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  margin: 16px 0 4px 0;
}

.no-contacts-subheader {
  width: 50%;
  margin: auto;
  color: $gray-800;
}

.contacts-selected {
  bottom: 0;
  display: flex;
  justify-content: space-between;
  background: $truegray-100;
  padding: 12px 16px;
  border-radius: 4px;
}

.search-circle {
  font-size: 20px;
  width: 60px;
  height: 60px;
  background: #f4ebff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
  color: $indigo-500;
  border: 8px solid #f9f5ff;
}

.tag {
  margin: 0 4px 2px 0;
  display: inline-block;
}

.table-filter-button {
  margin-right: 2px;
}

:deep(.data-table table > tbody > tr > td) {
  vertical-align: middle;
}
</style>
